/**
 * When the launcher teaser ("Chatte mit uns!") may appear.
 *
 * The teaser waits until the visitor has scrolled 60 % of the viewport height,
 * so it does not cover the page on arrival. There is deliberately no time-based
 * fallback: pages too short to scroll that far, and hosts whose embed script
 * predates scroll reporting, never show the teaser (#104422).
 */

/** Share of the viewport height the visitor has to scroll past. */
export const TEASER_SCROLL_RATIO = 0.6;

export interface ScrollSnapshot {
  scrollY: number;
  viewportHeight: number;
  documentHeight: number;
}

export type ScrollListener = (snapshot: ScrollSnapshot) => void;

/** Subscribes to scroll snapshots and returns the unsubscribe function. */
export type ScrollSubscription = (listener: ScrollListener) => () => void;

const scrollThreshold = (viewportHeight: number): number =>
  viewportHeight * TEASER_SCROLL_RATIO;

export const hasPassedTeaserThreshold = ({
  scrollY,
  viewportHeight,
}: ScrollSnapshot): boolean => scrollY >= scrollThreshold(viewportHeight);

/**
 * Scroll source for the npm package, which is mounted straight into the host
 * page and therefore sees the page scroll itself.
 */
export const subscribeToWindowScroll: ScrollSubscription = (listener) => {
  const emit = (): void =>
    listener({
      scrollY: window.scrollY,
      viewportHeight: window.innerHeight,
      documentHeight: document.documentElement.scrollHeight,
    });

  window.addEventListener("scroll", emit, { passive: true });
  emit();

  return () => window.removeEventListener("scroll", emit);
};
